import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { PageHeader, AIInsightCard, Section, Loading } from "../components/Common";
import { Sparkles, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

/**
 * AI Executive Intelligence — strategic narrative surface.
 *
 * Rule of grounding: any quantitative value the executive can see on this
 * page must come from the Central Metrics Service (`execKpis`) — the AI is
 * only ever trusted for narrative reasoning, never for reproducing numbers.
 * The canonical KPI strip below is deterministically rendered from the same
 * snapshot the Executive Overview consumes, so numbers reconcile 1:1.
 */
export default function AIExecutive() {
  const [items, setItems] = useState([]);
  const [kpis, setKpis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);

  // Deterministic single-current-briefing selection (see Executive.jsx).
  const pickCurrentBriefing = (raw) => {
    if (!Array.isArray(raw) || raw.length === 0) return [];
    const kindFor = (title = "") => {
      const t = title.toLowerCase();
      if (t.includes("today") || t.includes("daily")) return "today";
      if (t.includes("week")) return "week";
      if (t.includes("month")) return "month";
      if (t.includes("quarter")) return "quarter";
      if (t.includes("annual") || t.includes("year")) return "year";
      return "briefing";
    };
    const byKind = new Map();
    for (const it of raw) {
      const k = kindFor(it.title);
      const cur = byKind.get(k);
      if (!cur || (it.generated_at || "") > (cur.generated_at || "")) byKind.set(k, it);
    }
    const candidates = [...byKind.values()].sort(
      (a, b) => (b.generated_at || "").localeCompare(a.generated_at || "")
    );
    return candidates.slice(0, 1);
  };

  useEffect(() => {
    Promise.all([
      api.execInsights().then(({ data }) => setItems(pickCurrentBriefing(data.insights || []))),
      api.execKpis().then(({ data }) => setKpis(data)),
    ]).finally(() => setLoading(false));
  }, []);

  const gen = async () => {
    setGenerating(true);
    try {
      const { data } = await api.execGenerate();
      // Replace (never append) the current briefing.
      setItems([data.insight]);
      toast.success("New AI briefing generated");
    } catch {
      toast.error("Unable to generate at this time");
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div data-testid="ai-executive-page">
      <PageHeader
        kicker="AI Executive Intelligence"
        title="Strategic Analysis & Recommendations"
        subtitle="Board-ready narrative analysis. Every quantitative figure below is deterministically taken from the Central Metrics Service — AI is used for interpretation, never for reproducing numbers."
        actions={
          <button
            onClick={gen}
            disabled={generating}
            className="hl-btn-accent inline-flex items-center gap-2 text-sm disabled:opacity-60"
            data-testid="ai-generate"
          >
            <Sparkles className="w-4 h-4" />
            {generating ? "Generating…" : "New briefing"}
          </button>
        }
      />

      {/* Canonical KPI strip — the authoritative snapshot the narrative below
          must reconcile with. This block is NOT AI-generated. */}
      {kpis && (
        <div className="hl-card p-5 mb-6" data-testid="ai-canonical-strip">
          <div className="flex items-center gap-2 mb-3">
            <ShieldCheck className="w-4 h-4 text-emerald-600" />
            <span className="text-[10px] uppercase tracking-[0.28em] font-bold text-[color:var(--hl-navy)]">
              Canonical Snapshot · Current actual
            </span>
            <span className="ml-auto text-[10px] uppercase tracking-widest text-slate-400">
              Source · Central Metrics Service
            </span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500">Enrolment · Current roll</div>
              <div className="font-display font-bold text-xl text-[color:var(--hl-navy)]" data-testid="ai-canonical-enrolment">{kpis.students}</div>
              <div className="text-[10px] text-slate-500">{kpis.boys} boys · {kpis.girls} girls</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500">Attendance · {kpis.attendance_period_label || "Term to date"}</div>
              <div className="font-display font-bold text-xl text-[color:var(--hl-navy)]" data-testid="ai-canonical-attendance">{kpis.attendance_pct}%</div>
              <div className="text-[10px] text-slate-500">
                {kpis.attendance_target_pct ? `target ${kpis.attendance_target_pct}%` : "authoritative roll"}
              </div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500">Grade average · {kpis.grade_period_label || "Term to date"}</div>
              <div className="font-display font-bold text-xl text-[color:var(--hl-navy)]" data-testid="ai-canonical-grade">{kpis.grade_avg}%</div>
              <div className="text-[10px] text-slate-500">weighted total</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500">Fee collection · {kpis.finance.collection_period_label || "Year to date"}</div>
              <div className="font-display font-bold text-xl text-[color:var(--hl-navy)]" data-testid="ai-canonical-collection">{kpis.finance.collection_pct}%</div>
              <div className="text-[10px] text-slate-500">
                {kpis.finance.currency_symbol || "$"}{Number(kpis.finance.outstanding || 0).toLocaleString()} outstanding
              </div>
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <Loading />
      ) : (
        <Section
          title="AI Analysis · Advisory"
          subtitle="AI-generated interpretation of the canonical snapshot above. Advisory only — final decisions rest with authorised school leadership."
        >
          {items.length === 0 ? (
            <div className="hl-card hl-ai-card p-5 text-sm text-slate-600" data-testid="ai-empty">
              No AI briefing yet. Generate one to preview a strategic executive briefing based on the current canonical snapshot.
            </div>
          ) : (
            <AIInsightCard
              title={items[0].title}
              body={items[0].body}
              severity={items[0].severity}
              testId={`ai-item-${items[0].id}`}
            />
          )}
        </Section>
      )}
    </div>
  );
}
